/**
 * One acknowledgment cycle — §7.7's "the owner sees an N-of-M view".
 *
 * The page serves two readers at once and does not pretend otherwise. The owner
 * (and anyone who can publish the document) gets the numbers and the roster; a
 * person who merely owes the read gets the same page with `progress` nulled out
 * and their own line intact. `progressVisible` on the response says which of the
 * two answers came back, and {@link CycleProgressView} renders the redaction as
 * a sentence rather than as an empty bar — "you are not allowed to know" and
 * "nobody has signed" must never look the same.
 *
 * The roster sorts outstanding people first, because that is the list an owner
 * acts on, and it includes people who signed and have since left the audience
 * rather than dropping them. Dropping them would make the roster disagree with
 * the count beside it, and somebody would go looking for a missing signature.
 */

import { Link } from '@tanstack/react-router';
import { useAcknowledgmentCycle } from '../../api/queries.ts';
import type { AcknowledgmentRosterEntry } from '../../api/types.ts';
import { formatDateTime } from '../../components/format.ts';
import { Badge, RoleBadge, StatusBadge } from '../../components/ui/badge.tsx';
import { Loading, PageError } from '../../components/ui/feedback.tsx';
import {
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  Mono,
  PageHeader,
} from '../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';
import { DocumentStatusBadge } from '../documents/vocabulary.tsx';
import { AcknowledgeAction } from './acknowledge.tsx';
import { CycleProgressView } from './progress.tsx';
import { AudienceDescription, CycleScopeBadge, DueDate } from './vocabulary.tsx';

function RosterRow({ entry }: { entry: AcknowledgmentRosterEntry }) {
  return (
    <TR>
      <TD>
        <div className="flex flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/people/$personId"
              params={{ personId: entry.personId }}
              className="font-medium text-primary no-underline hover:underline"
            >
              {entry.name}
            </Link>
            {entry.status !== 'active' ? <StatusBadge status={entry.status} /> : null}
          </span>
          <span className="text-xs text-tertiary">{entry.email}</span>
        </div>
      </TD>
      <TD>
        <RoleBadge role={entry.role} />
      </TD>
      <TD>
        {entry.acknowledgedAt ? (
          <Badge tone="success">Acknowledged</Badge>
        ) : (
          <Badge tone="warning">Outstanding</Badge>
        )}
      </TD>
      <TD className="text-xs text-tertiary">
        {entry.acknowledgedAt ? formatDateTime(entry.acknowledgedAt) : '—'}
      </TD>
      <TD>
        {entry.inAudience ? null : (
          <Badge tone="outline">
            <span title="Signed, and no longer resolves into the audience — a changed role or a revoked grant. The row is retained because it records something that happened.">
              No longer in the audience
            </span>
          </Badge>
        )}
      </TD>
    </TR>
  );
}

export function AcknowledgmentCyclePage({ requestId }: { requestId: string }) {
  const query = useAcknowledgmentCycle(requestId);

  if (query.isPending) return <Loading label="Resolving the audience…" />;
  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Acknowledgment"
        back={{ href: '/acknowledgments', label: 'What you owe' }}
      />
    );
  }

  const { cycle, people } = query.data;

  return (
    <>
      <PageHeader
        title={cycle.title}
        description={
          <>
            Acknowledgment is scoped to a version, never to a document (§7.7). This cycle is about
            version {cycle.versionNumber} and nothing else — publishing a revision opens a new one,
            and these signatures do not carry into it.
          </>
        }
        actions={
          <Link
            to="/documents/$nodeId"
            params={{ nodeId: cycle.documentId }}
            className="inline-flex h-10 items-center rounded-lg border border-primary bg-primary px-3.5 text-sm font-semibold text-secondary no-underline shadow-xs hover:bg-primary-hover"
          >
            Read the document
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <CycleScopeBadge cycle={cycle} />
        <DocumentStatusBadge status={cycle.documentStatus} />
        <span className="text-sm text-tertiary">
          <DueDate dueDate={cycle.dueDate} />
        </span>
      </div>

      <AcknowledgeAction cycle={cycle} />

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <h2 className="mb-3 text-md font-semibold text-primary">Progress</h2>
          <CycleProgressView progress={cycle.progress} />
        </Card>

        <Card>
          <h2 className="mb-4 text-md font-semibold text-primary">This cycle</h2>
          <DefinitionList>
            <Definition term="Audience">
              <AudienceDescription audience={cycle.audience} />
            </Definition>
            <Definition term="Version">
              v{cycle.versionNumber} <Mono>{cycle.versionId}</Mono>
            </Definition>
            <Definition term="Due">
              <DueDate dueDate={cycle.dueDate} />
            </Definition>
            <Definition term="Opened">{formatDateTime(cycle.createdAt)}</Definition>
            <Definition term="You">
              {cycle.viewer.inAudience
                ? cycle.viewer.acknowledged
                  ? 'In the audience, and you have acknowledged it.'
                  : 'In the audience, and you have not acknowledged it.'
                : 'Not in this audience.'}
            </Definition>
          </DefinitionList>
        </Card>
      </div>

      {people ? (
        <Card padded={false}>
          <CardHeader
            title="Who owes it"
            description="Outstanding first — that is the list to act on. People who signed and have since left the audience are kept at the bottom rather than dropped, so this adds up to the count above."
          />
          <Table>
            <THead>
              <TR>
                <TH>Person</TH>
                <TH>Role</TH>
                <TH>State</TH>
                <TH>Acknowledged</TH>
                <TH>{''}</TH>
              </TR>
            </THead>
            <TBody>
              {people.length === 0 ? (
                <TableEmpty colSpan={5}>
                  Nobody resolves into this audience right now. A cycle opened before anybody holds
                  the grant is legitimate — it is auto-enrolment seen from the start.
                </TableEmpty>
              ) : (
                people.map((entry) => <RosterRow key={entry.personId} entry={entry} />)
              )}
            </TBody>
          </Table>
        </Card>
      ) : null}
    </>
  );
}
